<template src="./calculated.html"></template>

<script>
  import selectmodal from '../selectoptions/selectoptions'
  import selectmaterialmodal from '../selectpipe/selectpipe'
  import SubNavigationBar from '../../../common/SubNavigationBar.vue'
  import TaskModalCostSummary from '../common/TaskModalCostSummary.vue'
  import ExcavationDimensionFields from './ExcavationDimensionFields.vue'
  import axios from 'axios'
  import { grossProfit, computeTaskMaterialBuyCostFromCalculations, computeTaskMaterialSellFromCalculations, productMarkupOnCostPercent } from '../../../../helpers/quoteMargin'
  import { bankersRound } from '../../../../helpers/quoteTotals'
  import {
    applyResolvedSellToMaterial,
    accountMarkupPctForItem,
    catalogMaterialId,
    mergeCatalogPricingOntoLineItem,
    resolvedCatalogItemSell,
    DEFAULT_STANDARD_MARGIN_PCT
  } from '../../../../helpers/productPricing'
  import compareModalMixin from '../../../../helpers/compareModalMixin'
  import { resolveTaskSellPrice, TASK_PRICE_TYPES } from '../../../../helpers/taskPricing'
  import {
    getCalculatedLabels,
    isExcavationTemplate,
    isFlashingTemplate,
    normalizeTemplate,
    CUT_FILL_TYPES,
    excavationMinutesPerM3,
    soilTypesForMethod,
    backfillOpsFactor,
    SOIL_TYPE_HELP_ITEMS
  } from '../../../../helpers/taskTypes'

  export default {
    mixins: [compareModalMixin],
    components: {
      'app-select-modal': selectmodal,
      'app-select-material-modal': selectmaterialmodal,
      'app-sub-navigation-bar': SubNavigationBar,
      'app-task-modal-cost-summary': TaskModalCostSummary,
      'app-excavation-dimension-fields': ExcavationDimensionFields
    },
    watch: {
      'task.sell_price': 'onCompareFieldsMaybeChanged'
    },
    props: {
      parent_task: {
        type: Object
      },
      parent_addTask: {
        type: Function
      },
      breadcrumbSegments: {
        type: Array,
        default: function () { return [] }
      },
      catalogViewMode: {
        type: String,
        default: 'browse'
      },
      isAtCategoryTreeRoot: {
        type: Boolean,
        default: true
      }
    },
    data: function () {
      return {
        editDescription: false,
        editTitle: false,
        fittingsModal: false,
        fixingsModal: false,
        materialModal: false,
        task: {
          id: '',
          code: '',
          calculations: [],
          sell_price: 0,
          labour_time_hours: 0,
          labour_time_minutes: 0,
          qty: 1,
        },
        materials: [],
        pipes: [],
        fixings: [
          {
            qty: 0
          }
        ],
        fittings: [
          {
            qty: 0
          }
        ],
        options: [
          {
            qty: 0
          }
        ],
        calculation: {
          id: '',
          location: 1,
          materials: {},
          fittings: [],
          fixings: [],
          material: {
            time_complete: 0,
            sell_price: 0
          },
          multiplier: 1,
          total: 0
        },
        loading: false,
        showSoilTypeHelp: false,
        soilTypeHelpItems: SOIL_TYPE_HELP_ITEMS,
        standardMarginPct: DEFAULT_STANDARD_MARGIN_PCT,
        catalogByMaterialId: {},
        fetchedMaterialCache: {},
        calculateIntervalId: null,
        expandedCalculations: {},
        priceType: TASK_PRICE_TYPES.STANDARD,
        catalogPriceOverrides: {
          sell_price: 0,
          addon_sell_price: 0
        },
        difficulty: [
          {id: 1, name: 'x1'},
          {id: 1.25, name: 'x1.25'},
          {id: 1.5, name: 'x1.5'},
          {id: 1.75, name: 'x1.75'},
          {id: 2, name: 'x2'},
          {id: 2.25, name: 'x2.25'},
          {id: 2.5, name: 'x2.5'},
          {id: 2.75, name: 'x2.75'},
          {id: 3, name: 'x3'},
          {id: 3.25, name: 'x3.25'},
          {id: 3.5, name: 'x3.5'},
          {id: 3.75, name: 'x3.75'},
          {id: 4, name: 'x4'},
          {id: 4.25, name: 'x4.25'},
          {id: 4.5, name: 'x4.5'},
          {id: 4.75, name: 'x4.75'},
          {id: 5, name: 'x5'}
        ]
      }
    },
    computed: {
      taskBreadcrumbCode: function () {
        const task = this.task || {}
        if (task.code != null && String(task.code).trim() !== '') {
          return String(task.code).trim()
        }
        if (task.uuid != null && String(task.uuid).trim() !== '') {
          return String(task.uuid).trim()
        }
        return ''
      },
      calculatedTotal: function () {
        const sp = Number(this.task.sell_price) || 0
        return sp
      },
      standardPrice: function () {
        // Calculated tasks always show the live recalculated total — catalog sell_price
        // must not freeze the hero price when fixings/difficulty change.
        return this.calculatedTotal
      },
      addonPrice: function () {
        return resolveTaskSellPrice(
          {
            sell_price: null,
            addon_sell_price: this.catalogPriceOverrides && this.catalogPriceOverrides.addon_sell_price
          },
          TASK_PRICE_TYPES.ADDON,
          this.calculatedTotal
        )
      },
      activePrice: function () {
        return this.priceType === TASK_PRICE_TYPES.ADDON ? this.addonPrice : this.standardPrice
      },
      price: function () {
        const n = this.activePrice
        return Number.isFinite(n) ? n.toFixed(2) : '0.00'
      },
      materialsCost: function () {
        if (!this.task.calculations || !this.task.calculations.length) return 0
        return computeTaskMaterialSellFromCalculations(
          this.task.calculations,
          Number(this.task.qty) || 1,
          this.standardMarginPct
        )
      },
      materialsBuyCost: function () {
        if (!this.task.calculations || !this.task.calculations.length) return 0
        return computeTaskMaterialBuyCostFromCalculations(
          this.task.calculations,
          Number(this.task.qty) || 1
        )
      },
      displayGrossProfit: function () {
        const sell = parseFloat(this.price)
        const buy = this.materialsBuyCost
        if (!Number.isFinite(sell)) return '-'
        return grossProfit(sell, buy).toFixed(2)
      },
      displayProductMarginPct: function () {
        const calcs = this.task.calculations || []
        let totalBuy = 0
        let weightedMarkup = 0
        calcs.forEach((calc) => {
          const mult = Number(calc.multiplier) || 0
          if (!calc.material || mult <= 0) return
          const buy = (parseFloat(calc.material.cost) || 0) * mult
          if (buy <= 0) return
          weightedMarkup += accountMarkupPctForItem(calc.material, this.standardMarginPct) * buy
          totalBuy += buy
        })
        if (totalBuy > 0) {
          return bankersRound(weightedMarkup / totalBuy, 1)
        }
        const sell = this.materialsCost
        const buy = this.materialsBuyCost
        if (!sell || !Number.isFinite(sell)) return '-'
        return productMarkupOnCostPercent(sell, buy)
      },
      displayGstAmount: function () {
        const sell = parseFloat(this.price)
        if (!Number.isFinite(sell) || sell <= 0) return '0.00'
        return bankersRound(sell / 11, 2).toFixed(2)
      },
      displayLabourRate: function () {
        const hourly = Number(this.task.labour_hourly_rate)
        if (!Number.isFinite(hourly) || hourly <= 0) return ''
        return '$' + hourly.toFixed(2) + '/hr'
      },
      labourTimeFormatted: function () {
        const hours = Number(this.task.labour_time_hours) || 0
        const minutes = Number(this.task.labour_time_minutes) || 0
        if (hours > 0 && minutes > 0) {
          return hours + 'hr ' + minutes + 'min'
        }
        if (hours > 0) {
          return hours + 'hr'
        }
        return minutes + 'min'
      },
      taskTemplate: function () {
        return normalizeTemplate(this.task.template, 'calculated')
      },
      calcLabels: function () {
        return getCalculatedLabels(this.taskTemplate)
      },
      isExcavation: function () {
        return isExcavationTemplate(this.taskTemplate)
      },
      showFittings: function () {
        return this.calcLabels.hasFittings !== false
      },
      showFixings: function () {
        return this.calcLabels.hasFixings !== false
      },
      cutFillTypeOptions: function () {
        return CUT_FILL_TYPES
      },
      isFlashing: function () {
        return isFlashingTemplate(this.taskTemplate)
      }
    },
    methods: {
      buildCompareFieldSignature: function () {
        const t = this.task
        return [t.title, t.description, t.qty, t.sell_price, this.priceType].join('\u0001')
      },
      getCompareDifficultyLabel: function () {
        return '–'
      },
      onDifficultySelected: function (calculation, event) {
        if (!calculation || !event) return
        this.$set(calculation, 'location', event.id)
        this.calculate()
      },
      calculate: function () {
        const hourly = Number(this.task.labour_hourly_rate)
        const ratePerMin = (Number.isFinite(hourly) ? hourly : 0) / 60
        let labourMinutes = 0
        let total = 0
        const qty = Number(this.task.qty) || 1

        if (this.isExcavation) {
          for (let c = 0; c < this.task.calculations.length; c++) {
            const calculation = this.task.calculations[c]
            const V = (Number(calculation.length) || 0) *
              (Number(calculation.width) || 0) *
              (Number(calculation.depth) || 0)
            const method = this.task.excavation_method || 'hand'
            const soil = calculation.soil_type || 'medium'
            const perM3 = excavationMinutesPerM3(method, soil)
            const ops = backfillOpsFactor(calculation.cut_fill_type || 'cut')
            const calcQty = Number(calculation.qty) || 1
            const labourMins = V * perM3 * ops * calcQty
            calculation.total = ratePerMin * labourMins
            labourMinutes += labourMins
            total += calculation.total
          }
        } else {
          for (let c = 0; c < this.task.calculations.length; c++) {
            const calculation = this.task.calculations[c]
            let fittingsTotal = 0
            let fixingsTotal = 0
            const diff = Number(calculation.location) || 1
            const mat = calculation.material || { sell_price: 0, time_complete: 0 }
            const matSell = resolvedCatalogItemSell(mat, this.standardMarginPct)
            const matTime = Number(mat.time_complete) || 0
            const mult = Number(calculation.multiplier) || 0

            if (this.showFittings && calculation.fittings && Array.isArray(calculation.fittings)) {
              for (let f = 0; f < calculation.fittings.length; f++) {
                const fit = calculation.fittings[f]
                const q = Number(fit.qty) || 0
                if (q > 0) {
                  const t = Number(fit.time_complete) || 0
                  const sp = resolvedCatalogItemSell(fit, this.standardMarginPct)
                  labourMinutes += (t * q) * diff
                  fittingsTotal += (sp + ratePerMin * (t * diff)) * q
                }
              }
            }

            if (this.showFixings && calculation.fixings && Array.isArray(calculation.fixings)) {
              for (let f = 0; f < calculation.fixings.length; f++) {
                const fix = calculation.fixings[f]
                const q = Number(fix.qty) || 0
                if (q > 0) {
                  const t = Number(fix.time_complete) || 0
                  const sp = resolvedCatalogItemSell(fix, this.standardMarginPct)
                  labourMinutes += (t * q) * diff
                  fixingsTotal += (sp + ratePerMin * (t * diff)) * q
                }
              }
            }

            labourMinutes += (matTime * mult) * diff
            const lineMaterial = (matSell + ratePerMin * (matTime * diff)) * mult
            calculation.total = lineMaterial + fittingsTotal + fixingsTotal
            total += calculation.total
          }
        }

        labourMinutes = labourMinutes * qty
        const hours = Math.floor(labourMinutes / 60)
        const minutes = Math.floor(labourMinutes % 60)
        total = total * qty

        this.$set(this.task, 'labour_time_hours', hours)
        this.$set(this.task, 'labour_time_minutes', minutes)
        this.$set(this.task, 'sell_price', Number.isFinite(total) ? total : 0)
      },
      addItem: function () {
        const id = this.task.calculations.length + 1
        if (this.isExcavation) {
          this.task.calculations.push({
            id: id,
            cut_fill_type: 'cut_and_fill',
            soil_type: 'medium',
            length: '',
            width: '',
            depth: '',
            qty: 1,
            total: 0
          })
        } else {
          this.task.calculations.push({
            id: id,
            location: 1,
            multiplier: 0,
            material: {
              time_complete: 0,
              sell_price: 0
            },
            fittings: [],
            fixings: [],
            total: 0
          })
        }
        this.$set(this.expandedCalculations, id, true)
      },
      addTask: function () {
        this.calculate()
        this.task.template = this.taskTemplate
        this.task.price_type = this.priceType
        this.task.sell_price = this.price
        this.parent_addTask(this.task)
        this.$emit('close')
      },
      onCutFillTypeSelected: function (calculation, event) {
        if (!calculation || !event) return
        this.$set(calculation, 'cut_fill_type', event.id)
        this.calculate()
      },
      soilOptionsForCalc: function () {
        return soilTypesForMethod((this.task && this.task.excavation_method) || 'hand')
      },
      onSoilTypeSelected: function (calculation, event) {
        if (!calculation || !event) return
        this.$set(calculation, 'soil_type', event.id)
        this.calculate()
      },
      onCalcQtySelected: function (calculation, event) {
        if (!calculation || !event) return
        this.$set(calculation, 'qty', Number(event.id) || 1)
        this.calculate()
      },
      deleteCalculation: function (id) {
        if (this.task.calculations.length <= 1) {
          this.warningNotification('You cannot delete the last calculation.')
          return
        }
        this.removeById(this.task.calculations, 'id', id)
        this.$delete(this.expandedCalculations, id)
      },
      showPipes: function (calculation) {
        this.calculation = calculation
        this.materialModal = true
      },
      openFittings: function (calculation) {
        this.calculation = calculation
        this.options = this.fittings.map((fitting) => {
          const row = Object.assign({}, fitting)
          row.qty = Number(row.qty) || 0
          if (calculation.fittings && calculation.fittings.length) {
            const found = calculation.fittings.find(
              (i) => String(i.id) === String(fitting.id) && Number(i.qty) > 0
            )
            if (found) row.qty = Number(found.qty) || 0
          }
          return row
        })
        this.fittingsModal = true
      },
      openFixings: function (calculation) {
        this.calculation = calculation
        // Always start from full catalog; do not replace with calculation.fixings alone (often [] — modal looked empty).
        this.options = this.fixings.map((fixing) => {
          const row = Object.assign({}, fixing)
          row.qty = Number(row.qty) || 0
          if (calculation.fixings && calculation.fixings.length) {
            const found = calculation.fixings.find(
              (i) => String(i.id) === String(fixing.id) && Number(i.qty) > 0
            )
            if (found) row.qty = Number(found.qty) || 0
          }
          return row
        })
        this.fixingsModal = true
      },
      addMaterial: function (data) {
        const resolved = applyResolvedSellToMaterial(data.payload, this.standardMarginPct)
        data.calculation.material = resolved
        this.calculate()
      },
      addFittings: function (data) {
        data.calculation.fittings = this.normalizeSelectedItems(data.payload).map((item) => {
          return applyResolvedSellToMaterial(item, this.standardMarginPct)
        })
        this.calculate()
      },
      addFixings: function (data) {
        data.calculation.fixings = this.normalizeSelectedItems(data.payload).map((item) => {
          return applyResolvedSellToMaterial(item, this.standardMarginPct)
        })
        this.calculate()
      },
      normalizeSelectedItems: function (payload) {
        return (payload || [])
          .filter(function (item) { return Number(item.qty) > 0 })
          .map(function (item) {
            return Object.assign({}, item, { qty: Number(item.qty) || 0 })
          })
      },
      calculationHasProduct: function (calculation) {
        return !!(calculation.material && calculation.material.description)
      },
      activeFixings: function (calculation) {
        if (!calculation.fixings || !calculation.fixings.length) return []
        return calculation.fixings.filter(function (item) {
          return Number(item.qty) > 0
        })
      },
      activeFittings: function (calculation) {
        if (!calculation.fittings || !calculation.fittings.length) return []
        return calculation.fittings.filter(function (item) {
          return Number(item.qty) > 0
        })
      },
      setFixingQty: function (calculation, item, qty) {
        this.setCalculationItemQty(calculation, 'fixings', item, qty)
      },
      setFittingQty: function (calculation, item, qty) {
        this.setCalculationItemQty(calculation, 'fittings', item, qty)
      },
      setCalculationItemQty: function (calculation, key, item, qty) {
        const amount = Number(qty) || 0
        if (!Array.isArray(calculation[key])) {
          this.$set(calculation, key, [])
        }
        const list = calculation[key]
        const idx = list.findIndex(function (row) {
          return String(row.id) === String(item.id)
        })
        if (amount <= 0) {
          if (idx !== -1) list.splice(idx, 1)
        } else if (idx !== -1) {
          this.$set(list, idx, Object.assign({}, list[idx], { qty: amount }))
        } else {
          list.push(Object.assign({}, item, { qty: amount }))
        }
        this.calculate()
      },
      isCalculationExpanded: function (id) {
        return this.expandedCalculations[id] !== false
      },
      toggleCalculationExpanded: function (id) {
        this.$set(this.expandedCalculations, id, !this.isCalculationExpanded(id))
      },
      initializeExpandedCalculations: function () {
        const expanded = {}
        ;(this.task.calculations || []).forEach(function (calc) {
          expanded[calc.id] = true
        })
        this.expandedCalculations = expanded
      },
      findObjectByKey: function (array, key, value) {
        for (let i = 0; i < array.length; i++) {
          if (array[i][key] === value) {
            return array[i]
          }
        }
        return null
      },
      removeById (array, key, value) {
        for (let i = 0; i < array.length; i++) {
          if (array[i][key] === value) {
            array.splice(i, 1)
          }
        }
      },
      pipeLabel: function (calculation) {
        if (calculation.material && calculation.material.description) {
          return calculation.material.description
        }
        return ''
      },
      fittingsCount: function (calculation) {
        let count = 0
        if (calculation.fittings) {
          for (let i = 0; i < calculation.fittings.length; i++) {
            count = count + calculation.fittings[i].qty
          }
        }
        if (count > 0) {
          return count + ' Selected'
        } else {
          return 'Select'
        }
      },
      fixingsCount: function (calculation) {
        let count = 0
        if (calculation.fixings) {
          for (let i = 0; i < calculation.fixings.length; i++) {
            count = count + calculation.fixings[i].qty
          }
        }
        if (count > 0) {
          return count + ' Selected'
        } else {
          return 'Select'
        }
      },
      fetchMaterials () {
        if (this.isExcavation) {
          this.loading = false
          this.calculate()
          return
        }
        if (!this.task.uuid) {
          this.loading = false
          console.warn('Calculated task has no uuid/code; cannot load materials.')
          return
        }
        this.loading = true
        Promise.all([
          this.loadAccountMargin(),
          axios.get('/api/tasks/materials/' + this.task.uuid)
        ]).then(([, response]) => {
          this.task.materials = response.data.materials || { default: [], company: [] }
          if (!Array.isArray(this.task.materials.default)) this.task.materials.default = []
          if (!Array.isArray(this.task.materials.company)) this.task.materials.company = []
          this.processMaterials()
          return this.syncCalculationMaterialPricing()
        }).catch((err) => {
          console.error('Failed to load task materials:', err && err.message ? err.message : err)
          this.loading = false
        })
      },
      loadAccountMargin: function () {
        return axios.get('/api/setting/settings').then((res) => {
          const pct = res.data && res.data.standard_margin_pct
          if (pct != null && pct !== '') {
            this.standardMarginPct = parseFloat(pct) || DEFAULT_STANDARD_MARGIN_PCT
          }
        }).catch(() => {})
      },
      catalogMaterialId: function (material) {
        return catalogMaterialId(material)
      },
      findCatalogMaterialByDescription: function (material) {
        if (!material || !material.description) return null
        const desc = String(material.description).trim().toLowerCase()
        if (!desc) return null
        const all = this.pipes.concat(this.fittings, this.fixings)
        for (let i = 0; i < all.length; i++) {
          if (String(all[i].description || '').trim().toLowerCase() === desc) return all[i]
        }
        return null
      },
      findCatalogMaterial: function (material) {
        const id = this.catalogMaterialId(material)
        if (id && this.catalogByMaterialId[id]) return this.catalogByMaterialId[id]
        if (id) {
          const all = this.pipes.concat(this.fittings, this.fixings)
          for (let i = 0; i < all.length; i++) {
            if (this.catalogMaterialId(all[i]) === id) return all[i]
          }
        }
        return this.findCatalogMaterialByDescription(material)
      },
      resolveCalculationLinePricing: function (item) {
        if (!item) return item
        const pct = this.standardMarginPct
        const id = this.catalogMaterialId(item)
        const catalog = (id && this.fetchedMaterialCache[id]) ||
          this.findCatalogMaterial(item) ||
          (id && this.catalogByMaterialId[id]) ||
          null
        const merged = catalog ? mergeCatalogPricingOntoLineItem(item, catalog) : item
        return applyResolvedSellToMaterial(merged, pct)
      },
      syncCalculationMaterialPricing: function () {
        const self = this
        const idsToFetch = []

        const queueFetch = function (material) {
          const id = self.catalogMaterialId(material)
          if (!id) return
          if (idsToFetch.indexOf(id) === -1) idsToFetch.push(id)
        }

        ;(this.task.calculations || []).forEach(function (calc) {
          if (calc.material) queueFetch(calc.material)
          ;['fittings', 'fixings'].forEach(function (key) {
            ;(calc[key] || []).forEach(function (item) {
              if (Number(item.qty) > 0) queueFetch(item)
            })
          })
        })

        const applyAll = function () {
          ;(self.task.calculations || []).forEach(function (calc) {
            if (calc.material) {
              self.$set(calc, 'material', self.resolveCalculationLinePricing(calc.material))
            }
            ;['fittings', 'fixings'].forEach(function (key) {
              ;(calc[key] || []).forEach(function (item, idx) {
                if (Number(item.qty) > 0) {
                  self.$set(calc[key], idx, self.resolveCalculationLinePricing(item))
                }
              })
            })
          })
          self.calculate()
          self.loading = false
        }

        if (idsToFetch.length === 0) {
          applyAll()
          return Promise.resolve()
        }

        return Promise.all(idsToFetch.map(function (id) {
          return axios.get('/api/materials/' + id).then(function (res) {
            const m = res.data && res.data.material
            if (m) self.$set(self.fetchedMaterialCache, id, m)
          }).catch(function () {})
        })).then(applyAll)
      },
      processMaterials () {
        /*
         * We add to the ID of the material to identify if its a default material or a company manteral.
         * This is so we can get the material by its type and ID.
         */
        this.pipes = []
        this.fittings = []
        this.fixings = []
        this.catalogByMaterialId = {}
        const registerCatalogItem = (item) => {
          const id = this.catalogMaterialId(item)
          if (id) this.catalogByMaterialId[id] = item
        }
        for (let m = 0; m < this.task.materials.default.length; m++) {
          if (this.task.materials.default[m].id.toString().indexOf('default') === -1) {
            this.task.materials.default[m].id = this.task.materials.default[m].id + ',default'
          }

          this.task.materials.default[m].qty = 0

          if (this.task.materials.default[m].parent_type === 1) {
            this.pipes.push(this.task.materials.default[m])
            registerCatalogItem(this.task.materials.default[m])
          } else if (this.task.materials.default[m].parent_type === 2) {
            this.fittings.push(this.task.materials.default[m])
            registerCatalogItem(this.task.materials.default[m])
          } else if (this.task.materials.default[m].parent_type === 3) {
            this.fixings.push(this.task.materials.default[m])
            registerCatalogItem(this.task.materials.default[m])
          }
        }
        for (let m = 0; m < this.task.materials.company.length; m++) {
          if (this.task.materials.company[m].id.toString().indexOf('company') === -1) {
            this.task.materials.company[m].id = this.task.materials.company[m].id + ',company'
          }

          this.task.materials.company[m].qty = 0

          if (this.task.materials.company[m].parent_type === 1) {
            this.pipes.push(this.task.materials.company[m])
            registerCatalogItem(this.task.materials.company[m])
          } else if (this.task.materials.company[m].parent_type === 2) {
            this.fittings.push(this.task.materials.company[m])
            registerCatalogItem(this.task.materials.company[m])
          } else if (this.task.materials.company[m].parent_type === 3) {
            this.fixings.push(this.task.materials.company[m])
            registerCatalogItem(this.task.materials.company[m])
          }
        }

          this.loading = false
      },
      onBreadcrumbNavigateAll: function () {
        this.$emit('breadcrumb-navigate-all')
      },
      onBreadcrumbNavigateSegment: function (idx) {
        this.$emit('breadcrumb-navigate-segment', idx)
      }
    },
    mounted: function () {
      document.body.classList.add('no-scroll')

      if (this.parent_task) {
        this.task = JSON.parse(JSON.stringify(this.parent_task))
        this.$set(this.task, 'template', normalizeTemplate(this.task.template, 'calculated'))
        this.catalogPriceOverrides = {
          // Do not freeze sell_price — calculate() owns the live total for this modal
          sell_price: null,
          addon_sell_price: this.parent_task.addon_sell_price
        }
        if (this.parent_task.price_type === TASK_PRICE_TYPES.ADDON) {
          this.priceType = TASK_PRICE_TYPES.ADDON
        }
      }
      if (this.task.show_variables === undefined) {
        this.$set(this.task, 'show_variables', true)
      }

      if (!this.task.item_id) {
        this.task.calculations = []
      }

      if (this.task.calculations.length === 0) {
        this.addItem()
      }

      // Method is a task-level setting (chosen in Settings). Default it for older tasks that
      // predate the field. Soil type stays per-row (job-specific) and is defaulted here too.
      if (isExcavationTemplate(this.task.template)) {
        if (!this.task.excavation_method) this.$set(this.task, 'excavation_method', 'hand')
        this.task.calculations.forEach((c) => {
          if (!c.soil_type) this.$set(c, 'soil_type', 'medium')
        })
      }

      this.initializeExpandedCalculations()

      const self = this
      this.loadAccountMargin()
        .then(function () { return self.fetchMaterials() })
        .finally(function () {
          self.calculateIntervalId = setInterval(self.calculate, 500)
        })
    },
    beforeUnmount: function () {
      document.body.classList.remove('no-scroll')
      if (this.calculateIntervalId) {
        clearInterval(this.calculateIntervalId)
      }
    }
  }
</script>

<style scoped>
  .ui.form .fields {
    padding: 12px;
    margin-bottom: 1em;
  }
</style>

<!-- vue-select: avoid >>> in scoped CSS (invalid for CSS parsers / breaks vue-loader in some setups) -->
<style>
.calculated-task-modal .vs__search,
.calculated-task-modal .vs__search:focus {
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  appearance: none !important;
  background: none !important;
  border: 1px solid transparent !important;
  border-left: none !important;
  box-shadow: none !important;
  color: var(--vs-search-input-color) !important;
  flex-grow: 1 !important;
  font-size: var(--vs-font-size) !important;
  line-height: var(--vs-line-height) !important;
  margin: 4px 0 0 !important;
  max-width: 100% !important;
  outline: none !important;
  padding: 0 7px !important;
  width: 0 !important;
  z-index: 1 !important;
}
</style>
